import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Shield, ChevronDown, ChevronRight, Copy, Check, Lock, Globe, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const METHOD_COLORS = {
  GET: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  POST: 'bg-blue-100 text-blue-700 border-blue-200',
  PUT: 'bg-amber-100 text-amber-700 border-amber-200',
  DELETE: 'bg-red-100 text-red-700 border-red-200',
};

const API_SECTIONS = [
  {
    title: 'Authentication',
    description: 'JWT-based authentication flow. External platforms authenticate via a two-step token exchange.',
    endpoints: [
      {
        method: 'POST', path: '/api/auth/external',
        summary: 'Exchange a platform JWT for a session token',
        description: 'Primary entry point. The external platform generates a JWT signed with PLATFORM_JWT_SECRET containing the user\'s email. This endpoint validates that JWT, looks up the user, and returns a short-lived session token (24h).',
        body: '{\n  "email": "user@company.com",\n  "platform_jwt_token": "<platform_signed_jwt>"\n}',
        response: '{\n  "session_token": "<session_jwt>",\n  "user": { "id": "...", "name": "...", "email": "...", "role": "..." }\n}',
        auth: false,
      },
      {
        method: 'GET', path: '/api/auth/validate-session',
        summary: 'Validate a session token and return user data',
        description: 'Called by the frontend on page load. Pass the session token as a query parameter. Returns the authenticated user object if valid.',
        params: [{ name: 'token', type: 'string', required: true, desc: 'The session_token received from /auth/external' }],
        response: '{\n  "id": "...", "name": "...", "email": "...",\n  "role": "admin", "zone": "north",\n  "assigned_businesses": ["biz_id_1"]\n}',
        auth: false,
      },
      {
        method: 'POST', path: '/api/auth/generate-platform-token',
        summary: 'Generate a test platform JWT (development only)',
        description: 'Generates a signed platform JWT for testing. Accepts an email and returns a token that can be used with /auth/external.',
        body: '{\n  "email": "admin@company.com"\n}',
        response: '{\n  "platform_jwt_token": "<jwt>"\n}',
        auth: false,
      },
    ],
  },
  {
    title: 'Users',
    description: 'Manage team members. Users are assigned roles and can be organized in a hierarchy.',
    endpoints: [
      { method: 'GET', path: '/api/users', summary: 'List all users', params: [
        { name: 'role', type: 'string', required: false, desc: 'Filter by role name' },
        { name: 'zone', type: 'string', required: false, desc: 'Filter by zone' },
        { name: 'status', type: 'string', required: false, desc: 'Filter by status' },
      ], auth: true },
      { method: 'GET', path: '/api/users/tree', summary: 'Get organization tree structure', auth: true },
      { method: 'GET', path: '/api/users/{user_id}', summary: 'Get user by ID', auth: true },
      { method: 'POST', path: '/api/users', summary: 'Create a new user', body: '{\n  "name": "John Doe",\n  "email": "john@company.com",\n  "role": "sales_executive",\n  "zone": "north",\n  "manager_id": "parent_user_id"\n}', auth: true },
      { method: 'PUT', path: '/api/users/{user_id}', summary: 'Update an existing user', auth: true },
      { method: 'DELETE', path: '/api/users/{user_id}', summary: 'Delete a user', auth: true },
    ],
  },
  {
    title: 'Business Places',
    description: 'Manage client locations and business places. New places can be requested through an approval workflow.',
    endpoints: [
      { method: 'GET', path: '/api/business-places', summary: 'List business places', params: [
        { name: 'zone', type: 'string', required: false, desc: 'Filter by zone' },
        { name: 'business_id', type: 'string', required: false, desc: 'Filter by business line' },
      ], auth: true },
      { method: 'POST', path: '/api/business-places', summary: 'Create a business place (admin)', auth: true },
      { method: 'POST', path: '/api/business-places/request', summary: 'Request a new business place (requires approval)', auth: true },
      { method: 'PUT', path: '/api/business-places/{place_id}', summary: 'Update a business place', auth: true },
      { method: 'DELETE', path: '/api/business-places/{place_id}', summary: 'Delete a business place', auth: true },
    ],
  },
  {
    title: 'Sales Points',
    description: 'Manage individual sales points within business places.',
    endpoints: [
      { method: 'GET', path: '/api/sales-points', summary: 'List sales points', params: [
        { name: 'sp_type', type: 'string', required: false, desc: 'Filter by type' },
        { name: 'business_id', type: 'string', required: false, desc: 'Filter by business line' },
      ], auth: true },
      { method: 'POST', path: '/api/sales-points', summary: 'Create a sales point', auth: true },
      { method: 'PUT', path: '/api/sales-points/{sp_id}', summary: 'Update a sales point', auth: true },
      { method: 'DELETE', path: '/api/sales-points/{sp_id}', summary: 'Delete a sales point', auth: true },
    ],
  },
  {
    title: 'Client Visits',
    description: 'Track and manage field visits to clients and business locations.',
    endpoints: [
      { method: 'GET', path: '/api/visits', summary: 'List visits', params: [
        { name: 'user_id', type: 'string', required: false, desc: 'Filter by user' },
        { name: 'status', type: 'string', required: false, desc: 'Filter by status' },
        { name: 'business_id', type: 'string', required: false, desc: 'Filter by business line' },
      ], auth: true },
      { method: 'POST', path: '/api/visits', summary: 'Create a visit record', auth: true },
      { method: 'PUT', path: '/api/visits/{visit_id}', summary: 'Update a visit', auth: true },
      { method: 'DELETE', path: '/api/visits/{visit_id}', summary: 'Delete a visit', auth: true },
    ],
  },
  {
    title: 'Travel Plans',
    description: 'Full travel plan lifecycle: create, approve, execute with GPS/selfie verification, and submit expenses.',
    endpoints: [
      { method: 'POST', path: '/api/travel-plans', summary: 'Create a new travel plan', params: [
        { name: 'user_id', type: 'string', required: true, desc: 'ID of the plan creator (query param)' },
      ], body: '{\n  "visit_type": "client_visit",\n  "purpose": "new_sale_point",\n  "business_id": "...",\n  "start_date": "2025-03-01",\n  "end_date": "2025-03-03",\n  "locations": [\n    {\n      "business_place_id": "...",\n      "planned_checkin": "09:00",\n      "planned_checkout": "17:00"\n    }\n  ],\n  "daily_expenses": [\n    { "day": 1, "date": "2025-03-01", "breakfast": 200, "lunch": 300, "dinner": 300, "night_stay": 500 }\n  ],\n  "notes": "Visit notes"\n}', auth: true },
      { method: 'GET', path: '/api/travel-plans', summary: 'List travel plans', params: [
        { name: 'user_id', type: 'string', required: false, desc: 'Filter by user' },
        { name: 'status', type: 'string', required: false, desc: 'Filter by status' },
        { name: 'business_id', type: 'string', required: false, desc: 'Filter by business line' },
      ], auth: true },
      { method: 'GET', path: '/api/travel-plans/{plan_id}', summary: 'Get a specific travel plan', auth: true },
      { method: 'PUT', path: '/api/travel-plans/{plan_id}/approve', summary: 'Approve or reject a travel plan (admin)', params: [
        { name: 'action', type: 'string', required: true, desc: '"approve" or "reject"' },
        { name: 'remarks', type: 'string', required: false, desc: 'Approval remarks' },
      ], auth: true },
      { method: 'PUT', path: '/api/travel-plans/{plan_id}/start', summary: 'Start executing a plan (with selfie + GPS)', params: [
        { name: 'selfie', type: 'string', required: false, desc: 'Base64 selfie image' },
        { name: 'lat', type: 'float', required: false, desc: 'GPS latitude' },
        { name: 'lng', type: 'float', required: false, desc: 'GPS longitude' },
      ], auth: true },
      { method: 'PUT', path: '/api/travel-plans/{plan_id}/checkin/{location_index}', summary: 'Check in at a location', auth: true },
      { method: 'PUT', path: '/api/travel-plans/{plan_id}/checkout/{location_index}', summary: 'Check out from a location', auth: true },
      { method: 'PUT', path: '/api/travel-plans/{plan_id}/complete', summary: 'Mark a travel plan as completed', auth: true },
      { method: 'PUT', path: '/api/travel-plans/{plan_id}/upload-bills', summary: 'Upload expense bill images (base64)', auth: true },
      { method: 'DELETE', path: '/api/travel-plans/{plan_id}', summary: 'Delete a travel plan', auth: true },
    ],
  },
  {
    title: 'Approvals',
    description: 'Manage approval workflow for business place requests.',
    endpoints: [
      { method: 'GET', path: '/api/approvals', summary: 'List pending approvals', params: [
        { name: 'status', type: 'string', required: false, desc: 'Filter by status' },
      ], auth: true },
      { method: 'PUT', path: '/api/approvals/{approval_id}/approve', summary: 'Approve a request', auth: true },
      { method: 'PUT', path: '/api/approvals/{approval_id}/reject', summary: 'Reject a request', auth: true },
    ],
  },
  {
    title: 'Business Lines',
    description: 'Manage business verticals (e.g., FASTag, Prepaid Cards). Admin only.',
    endpoints: [
      { method: 'GET', path: '/api/business-lines', summary: 'List all business lines', auth: true },
      { method: 'POST', path: '/api/business-lines', summary: 'Create a business line', body: '{\n  "name": "fastag",\n  "label": "FASTag",\n  "description": "FASTag business vertical",\n  "color": "#D97706"\n}', auth: true },
      { method: 'PUT', path: '/api/business-lines/{line_id}', summary: 'Update a business line', auth: true },
      { method: 'DELETE', path: '/api/business-lines/{line_id}', summary: 'Delete a business line', auth: true },
    ],
  },
  {
    title: 'Roles & Features',
    description: 'Manage role definitions and feature access control. Admin only.',
    endpoints: [
      { method: 'GET', path: '/api/features', summary: 'List all available features', auth: true },
      { method: 'GET', path: '/api/roles', summary: 'List all roles with their features', auth: true },
      { method: 'GET', path: '/api/roles/{role_name}/features', summary: 'Get features for a specific role', auth: true },
      { method: 'POST', path: '/api/roles', summary: 'Create a new role', auth: true },
      { method: 'PUT', path: '/api/roles/{role_id}', summary: 'Update a role', auth: true },
      { method: 'DELETE', path: '/api/roles/{role_id}', summary: 'Delete a role', auth: true },
    ],
  },
  {
    title: 'Dashboard & Analytics',
    description: 'Aggregate statistics and performance analytics.',
    endpoints: [
      { method: 'GET', path: '/api/dashboard/stats', summary: 'Get dashboard KPI stats', params: [
        { name: 'user_id', type: 'string', required: false, desc: 'Scope to user' },
        { name: 'business_id', type: 'string', required: false, desc: 'Scope to business line' },
      ], auth: true },
      { method: 'GET', path: '/api/performance/analytics', summary: 'Get performance analytics data', auth: true },
    ],
  },
  {
    title: 'Data Export',
    description: 'Export data as CSV files.',
    endpoints: [
      { method: 'GET', path: '/api/export/team', summary: 'Export team data as CSV', auth: true },
      { method: 'GET', path: '/api/export/visits', summary: 'Export visits data as CSV', auth: true },
      { method: 'GET', path: '/api/export/sales-points', summary: 'Export sales points as CSV', auth: true },
      { method: 'GET', path: '/api/export/performance', summary: 'Export performance data as CSV', auth: true },
    ],
  },
  {
    title: 'Data Import',
    description: 'Import data from external sources using configurable connectors.',
    endpoints: [
      { method: 'POST', path: '/api/import/config', summary: 'Save an import configuration', auth: true },
      { method: 'GET', path: '/api/import/configs', summary: 'List saved import configurations', auth: true },
      { method: 'POST', path: '/api/import/fetch', summary: 'Fetch data using an import configuration', auth: true },
      { method: 'GET', path: '/api/imports', summary: 'List import history', auth: true },
      { method: 'GET', path: '/api/imports/{import_id}', summary: 'Get import details', auth: true },
    ],
  },
];

const EndpointCard = ({ ep }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid={`endpoint-${ep.method.toLowerCase()}-${ep.path.replace(/[/{}\s]/g, '-')}`} className="border border-border/40 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50/50 transition-colors text-left"
      >
        <Badge className={`${METHOD_COLORS[ep.method]} text-[11px] font-mono font-bold px-2 py-0.5 border`}>{ep.method}</Badge>
        <code className="text-sm font-mono text-slate-700 flex-1 truncate">{ep.path}</code>
        {ep.auth && <Lock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/30 bg-slate-50/30">
          <p className="text-sm text-slate-600 pt-3">{ep.summary}</p>
          {ep.description && <p className="text-xs text-slate-500 leading-relaxed">{ep.description}</p>}

          {ep.params && ep.params.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Parameters</p>
              <div className="bg-white border border-border/30 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 border-b border-border/30">
                    <th className="text-left px-3 py-1.5 font-semibold text-slate-600">Name</th>
                    <th className="text-left px-3 py-1.5 font-semibold text-slate-600">Type</th>
                    <th className="text-left px-3 py-1.5 font-semibold text-slate-600">Required</th>
                    <th className="text-left px-3 py-1.5 font-semibold text-slate-600">Description</th>
                  </tr></thead>
                  <tbody>
                    {ep.params.map((p, i) => (
                      <tr key={i} className="border-b border-border/20 last:border-0">
                        <td className="px-3 py-1.5 font-mono text-blue-600">{p.name}</td>
                        <td className="px-3 py-1.5 text-slate-500">{p.type}</td>
                        <td className="px-3 py-1.5">{p.required ? <Badge className="bg-red-50 text-red-600 text-[9px]">Required</Badge> : <span className="text-slate-400">Optional</span>}</td>
                        <td className="px-3 py-1.5 text-slate-500">{p.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ep.body && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Request Body</p>
                <button onClick={() => handleCopy(ep.body)} className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600 transition-colors">
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Copy
                </button>
              </div>
              <pre className="bg-slate-900 text-slate-100 rounded-md p-3 text-xs font-mono overflow-x-auto leading-relaxed">{ep.body}</pre>
            </div>
          )}

          {ep.response && (
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Example Response</p>
              <pre className="bg-slate-800 text-emerald-300 rounded-md p-3 text-xs font-mono overflow-x-auto leading-relaxed">{ep.response}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SectionBlock = ({ section }) => {
  const [open, setOpen] = useState(false);

  return (
    <Card data-testid={`api-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`} className="border border-border/60 shadow-sm">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left"
      >
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              {section.title}
              <Badge variant="outline" className="text-[10px] font-normal">{section.endpoints.length} endpoints</Badge>
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">{section.description}</p>
          </div>
          {open ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
        </CardHeader>
      </button>
      {open && (
        <CardContent className="space-y-2 pt-0">
          {section.endpoints.map((ep, i) => <EndpointCard key={i} ep={ep} />)}
        </CardContent>
      )}
    </Card>
  );
};

export default function ApiDocs() {
  const { isAdmin } = useApp();
  const [search, setSearch] = useState('');

  if (!isAdmin) {
    return (
      <div data-testid="api-docs-page" className="space-y-6 fade-in">
        <Card className="border border-border/60"><CardContent className="p-12 text-center">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-700">Admin Access Only</h2>
          <p className="text-sm text-slate-400 mt-1">API documentation is restricted to Admin role.</p>
        </CardContent></Card>
      </div>
    );
  }

  const filtered = search.trim()
    ? API_SECTIONS.map(s => ({
        ...s,
        endpoints: s.endpoints.filter(ep =>
          ep.path.toLowerCase().includes(search.toLowerCase()) ||
          ep.summary.toLowerCase().includes(search.toLowerCase()) ||
          ep.method.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(s => s.endpoints.length > 0)
    : API_SECTIONS;

  const totalEndpoints = API_SECTIONS.reduce((sum, s) => sum + s.endpoints.length, 0);

  return (
    <div data-testid="api-docs-page" className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">API Documentation</h1>
        <p className="text-sm text-slate-500 mt-1">{totalEndpoints} endpoints across {API_SECTIONS.length} modules</p>
      </div>

      {/* Auth Flow Overview */}
      <Card className="border border-amber-200 bg-amber-50/50 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-bold text-amber-800 flex items-center gap-2">
            <Lock className="w-4 h-4" /> Authentication Flow
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-amber-700 leading-relaxed">
            This application uses a two-step JWT authentication flow. No traditional login screen exists. Access is controlled by external platform tokens.
          </p>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="px-2.5 py-1.5 bg-white border border-amber-200 rounded font-semibold text-amber-800">1. Platform generates JWT</span>
            <ChevronRight className="w-3 h-3 text-amber-400" />
            <span className="px-2.5 py-1.5 bg-white border border-amber-200 rounded font-semibold text-amber-800">2. POST /api/auth/external</span>
            <ChevronRight className="w-3 h-3 text-amber-400" />
            <span className="px-2.5 py-1.5 bg-white border border-amber-200 rounded font-semibold text-amber-800">3. Receive session_token</span>
            <ChevronRight className="w-3 h-3 text-amber-400" />
            <span className="px-2.5 py-1.5 bg-white border border-amber-200 rounded font-semibold text-amber-800">4. Open app with ?session_token=xxx</span>
          </div>
          <div className="bg-white border border-amber-200 rounded-md p-3">
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-2">Quick Test (cURL)</p>
            <pre className="text-[11px] font-mono text-slate-700 whitespace-pre-wrap leading-relaxed">{`# 1. Generate a platform token
curl -X POST /api/auth/generate-platform-token \\
  -H "Content-Type: application/json" \\
  -d '{"email": "admin@company.com"}'

# 2. Exchange for session token
curl -X POST /api/auth/external \\
  -H "Content-Type: application/json" \\
  -d '{"email": "admin@company.com", "platform_jwt_token": "<token_from_step_1>"}'

# 3. Open browser: <app_url>?session_token=<session_token>`}</pre>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="api-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search endpoints by path, method, or description..."
          className="pl-10"
        />
      </div>

      {/* API Sections */}
      <div className="space-y-4">
        {filtered.map((section, i) => <SectionBlock key={i} section={section} />)}
        {filtered.length === 0 && (
          <Card className="border border-border/60"><CardContent className="p-12 text-center">
            <Globe className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No endpoints match your search.</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
